import { z } from 'zod';

import type { PromoCodesPageStore } from '@ctct/state-client-layer';

import type { PromoCodesService } from './PromoCodesService';
import { type TranslationGetter } from '../../../types';

export class ValidationService {
  private PROMO_CODE_VALID_CHARS = /^[a-z0-9_]+$/i;

  constructor(
    private pageStore: PromoCodesPageStore,
    private t: TranslationGetter,
    private promoCodesService: PromoCodesService
  ) {}

  public api() {
    return {
      validate: () => this.validate(),
    };
  }

  public data() {
    return {
      getZodSchema: () => this.getZodSchema(),
    };
  }

  public handlers() {
    return {};
  }

  private validate() {
    this.pageStore.setState({
      promoCodeFormErrors: [],
    });

    const schema = this.getZodSchema();
    const state = this.pageStore.getState();
    const { error } = schema.safeParse({
      promocode: state.promoCodeTextFieldValue,
      validatedPromoCodes: this.promoCodesService.getValidatedPromoCodeNames(),
    });

    if (error) {
      const [issue] = error.issues;

      if (issue) {
        this.pageStore.setState({
          promoCodeFormErrors: [issue.message],
        });
      }
    }

    return !error;
  }

  private getZodSchema() {
    return z
      .object({
        promocode: this.getPromoCodeValidationSchema(),

        // No need to add complex validation here, since these values will already have been validated on the server.
        validatedPromoCodes: z.array(z.string()),
      })
      .refine(({ promocode, validatedPromoCodes }) => !validatedPromoCodes.includes(promocode), {
        message: this.t('application-layer.promo-codes.add.error-already-applied'),
      });
  }

  private getPromoCodeValidationSchema() {
    return this.createStringValidationSchema(1, 50, this.PROMO_CODE_VALID_CHARS);
  }

  private createStringValidationSchema(min: number, max: number, regex: RegExp) {
    return z
      .string()
      .trim()
      .regex(regex, {
        message: this.t('application-layer.form-errors.incorrect-chars'),
      })
      .min(min, {
        message: this.t('application-layer.form-errors.min-allowed', { count: min }),
      })
      .max(max, {
        message: this.t('application-layer.form-errors.max-allowed', { count: max }),
      });
  }
}
